import {
	ChangeDetectionStrategy,
	Component,
	Signal,
	computed,
	input,
	output,
	signal
} from '@angular/core';
import { CoreComponent } from '../core/core.component.js';
import { ImageCroppedEvent, base64ToFile } from 'ngx-image-cropper';

export type ImageAspectRatio = 'square' | '16 / 9' | '4 / 3' | '9 / 16' | 'freeform';

interface ICroppedImage {
	base64: string;
	file: Blob;
}

@Component({
    selector: 'lib-crop',
    templateUrl: './crop.component.html',
    styleUrls: ['./crop.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CropComponent extends CoreComponent {
	public readonly aspectRatio = input<ImageAspectRatio>('square');
	public readonly imageToCrop = input<File>();
	public readonly resizeToWidth = input<number | 0>();
	public readonly resizeToHeight = input<number | 0>();
	public readonly cropButtonTitle = input<string>('Crop & Upload');
	public readonly enableCroppedImagePreview = input<boolean>(false);
	public readonly isUploading = input<boolean>(false);

	public readonly imageCropped = output<File>();

	public readonly croppedImage = signal<ICroppedImage | undefined>(undefined);
	public readonly showImagePreview = computed<boolean>(() => {
		if (!this.enableCroppedImagePreview()) {
			return false;
		}
		if (this.isMobileView()) {
			return false;
		}
		return true;
	});

	public readonly aspectRatioCalculated: Signal<number> = computed(() => {
		if (this.aspectRatio() === 'square') {
			return 1 / 1;
		}
		if (this.aspectRatio() === '16 / 9') {
			return 16 / 9;
		}
		if (this.aspectRatio() === '4 / 3') {
			return 4 / 3;
		}
		if (this.aspectRatio() === '9 / 16') {
			return 9 / 16;
		}

		return 0;
	});

	constructor() {
		super();
	}

	handleCrop(): void {
		const croppedImage = this.croppedImage();
		const imageToCrop = this.imageToCrop();

		if (croppedImage && imageToCrop) {
			this.imageCropped.emit(this.blobToFile(croppedImage.file, imageToCrop.name));
			this.clearFiles();
		}
	}

	imageLoaded() {}
	cropperReady() {}
	loadImageFailed() {}

	async handleImageCropped(event: ImageCroppedEvent): Promise<void> {
		if (event.base64) {
			const blob = base64ToFile(event.base64);

			this.croppedImage.set({
				base64: event.base64,
				file: blob
			});
		}
	}

	private blobToFile(theBlob: Blob, fileName: string): File {
		const b: any = theBlob;
		// A Blob() is almost a File() - it's just missing the two properties below which we will add
		b.lastModifiedDate = new Date();
		b.name = fileName;

		// Cast to a File() type
		return theBlob as File;
	}

	private clearFiles(): void {
		this.croppedImage.set(undefined);
	}
}
